/**
 * PWA Update Prompt
 *
 * Shows notification when app update is available or when offline ready.
 * Rebuilt on shadcn Button and Card.
 */

import { RefreshCw, Wifi, WifiOff, X } from 'lucide-react';
import { usePWA } from '../../hooks/usePWA';
import { Button, Card } from '@/components/inputs';

const PWAPrompt = () => {
  const { needRefresh, offlineReady, isOnline, updateServiceWorker, closePrompt } = usePWA();

  if (needRefresh) {
    return (
      <Card className="fixed bottom-4 right-4 z-50 max-w-sm shadow-lg" contentClassName="p-4">
          <div className="flex items-start gap-3">
            <RefreshCw className="size-5 text-primary mt-0.5 shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium">Update Available</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                A new version is available. Reload to update.
              </p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" label="Reload" onClick={() => updateServiceWorker(true)} />
                <Button size="sm" variant="outline" label="Later" onClick={closePrompt} />
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              icon={X}
              onClick={closePrompt}
              aria-label="Dismiss"
            />
          </div>
      </Card>
    );
  }

  if (offlineReady) {
    return (
      <Card className="fixed bottom-4 right-4 z-50 max-w-sm shadow-lg" contentClassName="p-4">
          <div className="flex items-start gap-3">
            <Wifi className="size-5 text-color-success-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium">Ready for Offline Use</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                App has been cached and can work offline.
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              icon={X}
              onClick={closePrompt}
              aria-label="Dismiss"
            />
          </div>
      </Card>
    );
  }

  if (!isOnline) {
    return (
      <div className="fixed bottom-4 right-4 z-50 bg-color-warning-100 dark:bg-color-warning-900 text-color-warning-800 dark:text-color-warning-200 px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
        <WifiOff className="size-4" />
        <span className="text-sm font-medium">You are offline</span>
      </div>
    );
  }

  return null;
};

export default PWAPrompt;
